<template>
  <StudioDialog
    :title="title"
    size="large"
    :primaryActionTitle="primaryActionTitle"
    secondaryActionTitle="Cancel"
    :primaryActionDisabled="isUploadingNewFile || isPrimaryActionDisabled"
    @primaryAction="handleSaveClicked"
    @secondaryAction="$emit('close')"
    @close="$emit('close')"
  >
    <div class="tw-relative tw-flex tw-h-[calc(100%-5.5rem)] tw-flex-col">
      <div class="tw-flex tw-items-center tw-justify-between tw-border-b tw-px-2 tw-py-2">
        <div>
          <div v-if="!loading" class="tw-text-md">
            {{ resultsCountText }}
          </div>
        </div>
        <div class="tw-flex tw-items-center tw-gap-2">
          <StudioSearchInput :searchTerm="searchTerm" @onSearch="handleOnSearch" />
          <slot
            name="upload-button"
            :success="handleUploadSuccess"
            :init="() => (isUploadingNewFile = true)"
            :failure="() => (isUploadingNewFile = false)"
            :close="() => (isUploadingNewFile = false)"
          />
        </div>
      </div>
      <InfiniteScrollContainer
        class="tw-flex tw-flex-1 tw-flex-col tw-overflow-scroll"
        :pause="loadingMore"
        @scroll-bottom="handleScrollBottom"
      >
        <div v-for="asset in assets" :key="asset.id" class="tw-border-b">
          <slot
            name="assets-list-item"
            :asset="asset"
            :isSelected="isAssetSelected(asset)"
            :handleSelectAsset="handleSelectAsset"
            :handleDeleteSuccess="handleDeleteSuccess"
            :handleRenameSuccess="handleRenameSuccess"
          />
        </div>
        <div
          v-if="loading || isUploadingNewFile"
          class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center"
        >
          <StudioSpinner :coverContainer="false" size="small" :overlay="isUploadingNewFile" class="tw-m-auto" />
        </div>
        <div v-if="error" class="tw-m-4">
          <p class="tw-text-red-500">Error loading results</p>
        </div>
      </InfiniteScrollContainer>
    </div>
  </StudioDialog>
</template>

<script lang="ts">
import { SeriesResponse } from '@goldcast/api/backend';
import debounce from 'lodash.debounce';
import { defineComponent, PropType } from 'vue';
import { mapGetters } from 'vuex';

import { Asset, GetAssetsFn } from './assetLibraryTypes';

import InfiniteScrollContainer from '@/studio/studioLibrary/InfiniteScrollContainer.vue';
import StudioDialog from '@/studioComponents/StudioDialog.vue';
import StudioSearchInput from '@/studioComponents/StudioSearchInput.vue';
import StudioSpinner from '@/studioComponents/StudioSpinner.vue';

export default defineComponent({
  name: 'AssetLibraryDialog',
  components: {
    StudioDialog,
    StudioSpinner,
    StudioSearchInput,
    InfiniteScrollContainer
  },
  props: {
    title: {
      type: String,
      required: true
    },
    maxSelections: {
      type: Number,
      default: Infinity
    },
    initialSelectedAssets: {
      type: Array as PropType<Asset[]>,
      default: () => []
    },
    getAssetsFn: {
      type: Function as PropType<GetAssetsFn>,
      required: true
    },
    primaryActionTitle: {
      type: String,
      default: 'Save'
    },
    isPrimaryActionDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      assets: [] as Asset[],
      selectedAssets: [...this.initialSelectedAssets],
      loading: false,
      loadingMore: false,
      searchTerm: '',
      error: null,
      totalCount: 0,
      pageOffset: 0,
      pageLimit: 20,
      isUploadingNewFile: false
    };
  },
  mounted() {
    this.loadContent({ showLoader: true, resetResults: true });
  },
  computed: {
    ...mapGetters('studioEventList', {
      event: 'getCurrentEvent'
    }),
    ...mapGetters('studioTemplateList', {
      template: 'getCurrentTemplate'
    }),
    ...mapGetters('studioSeriesList', {
      getCurrentSeriesById: 'getCurrentSeriesById'
    }),
    series(): SeriesResponse | null {
      if (!this.$route.params.seriesId) {
        return null;
      }

      return this.getCurrentSeriesById(this.$route.params.seriesId);
    },
    isRepeatingEvent(): boolean {
      return this.series?.type === 'repeating';
    },
    resultsCountText(): string {
      if (this.totalCount === 0) {
        return 'No results';
      }
      if (this.totalCount === 1) {
        return '1 result';
      }

      return `${this.totalCount} results`;
    }
  },
  methods: {
    handleSelectAsset(asset: Asset): void {
      const selectedIndex = this.selectedAssets.findIndex(item => item.id === asset.id);

      if (selectedIndex > -1) {
        this.selectedAssets.splice(selectedIndex, 1);
        return;
      }

      if (this.selectedAssets.length >= this.maxSelections && this.maxSelections > 1) {
        return;
      }

      if (this.selectedAssets.length === 1 && this.maxSelections === 1) {
        this.selectedAssets = [];
      }

      this.selectedAssets.push(asset);
    },
    handleSaveClicked(): void {
      this.$emit('select', this.selectedAssets);
    },
    handleDeleteSuccess(asset: Asset): void {
      this.assets = this.assets.filter(item => item.id !== asset.id);
      this.selectedAssets = this.selectedAssets.filter(item => item.id !== asset.id);
      this.totalCount = this.totalCount - 1;
    },
    handleRenameSuccess(newAsset: Asset): void {
      this.assets = this.assets.map(item => {
        if (item.id === newAsset.id) {
          return newAsset;
        }

        return item;
      });
    },
    loadContent({ showLoader = false, resetResults = false }) {
      if (resetResults) {
        this.assets = [];
        this.pageOffset = 0;
      }

      if (this.assets.length === this.totalCount && this.totalCount > 0) {
        return;
      }

      if (showLoader) {
        this.loading = true;
      }

      if (this.assets.length > 0) {
        this.loadingMore = true;
      }

      let workspaceId = '';
      if (this.$route.params.eventId || this.isRepeatingEvent) {
        workspaceId = this.event?.workspace?.id;
      } else if (this.$route.params.templateId) {
        workspaceId = this.template?.workspace?.id;
      }
      const workspace__isnull: boolean = !workspaceId;
      this.getAssetsFn({
        search: this.searchTerm,
        offset: this.pageOffset,
        limit: this.pageLimit,
        workspace: workspaceId ?? '',
        workspace__isnull
      })
        .then(({ assets, totalCount }) => {
          this.error = null;
          this.totalCount = totalCount;
          this.assets = this.assets.concat(assets);
        })
        .catch(error => {
          this.error = error;
        })
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    handleScrollBottom(): void {
      this.pageOffset = this.pageLimit + this.pageOffset;
      this.loadContent({ showLoader: false, resetResults: false });
    },
    handleUploadSuccess(asset: Asset): void {
      if (!asset) {
        return;
      }
      this.assets.unshift(asset);
      this.totalCount = this.totalCount + 1;
      this.handleSelectAsset(asset);
      this.isUploadingNewFile = false;
    },
    isAssetSelected(asset: Asset) {
      if (this.selectedAssets.length === 0) {
        return false;
      }

      return this.selectedAssets.some(item => item.id === asset.id);
    },
    handleOnSearch: debounce(function (value) {
      // @ts-ignore
      this.searchTerm = value;
      // @ts-ignore
      this.loadContent({ showLoader: false, resetResults: true });
    }, 200)
  }
});
</script>
